import * as React from 'react';
import { Redirect, useRouteMatch, useLocation } from 'react-router';

import { useQuery } from '@apollo/client';

import { PageLoadFailed, PageNotFound } from '@components/NotFound';
import { Loading } from '@containers/ProductDetailContainer/loading';

import { ForbiddenProductScreen } from './ForbiddenProductScreen';
import { getRcVersionKeys, IRCVersionKeys } from './getRcVersionKeys';
import { ProductDetail } from './ProductDetail';
import {
  getProductView_productDetailByMasterId,
  getProductView,
  getProductViewVariables,
} from './queries/__generated__/getProductView';
import getProductViewQuery from './queries/productView.graphql';
import { selectVariant } from './utils';

export interface IProductDetailRouteParams {
  productUrl: string;
  variantUrl?: string;
}

interface PDProps {
  productId: number;
  ssrEnabled: boolean;
}

const ProductDetailContainer: React.FC<PDProps> = ({
  productId,
  ssrEnabled,
}) => {
  const { params } = useRouteMatch<IProductDetailRouteParams>();
  const location = useLocation();

  const { data, loading, error } = useQuery<
    getProductView,
    getProductViewVariables
  >(getProductViewQuery, {
    ssr: ssrEnabled,
    variables: {
      masterId: productId.toString(),
    },

    errorPolicy: 'all',
  });

  if (!data?.productDetailByMasterId) {
    if (loading) {
      return <Loading productId={String(productId)} />;
    }
    if (error) {
      return <PageLoadFailed reason="unable to load PD data" />;
    }
    return null;
  }

  const { productDetailByMasterId } = data;
  let product: getProductView_productDetailByMasterId;
  try {
    // on ssr errors are not propagated (due to apollo bug) we need to check data
    product = productDetailByMasterId[0];
  } catch (e) {
    return <PageLoadFailed reason="unable to load PD data" />;
  }

  if (!product) {
    return <PageNotFound reason="Product not found" />;
  }

  const { variants } = product;
  const variant = selectVariant(variants, params);
  if (variant === undefined) {
    return <Redirect to={product.url} />;
  }
  const keys: IRCVersionKeys = getRcVersionKeys(location, params);

  if (product.isForbiddenToDisplay) {
    return <ForbiddenProductScreen product={product} />;
  }

  return (
    <ProductDetail
      PDView={data}
      rcKey={keys.rcKey}
      customRcKey={keys.customRcKey}
      customVersionKey={keys.customVersionKey}
    />
  );
};

export default ProductDetailContainer;
